import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { 
  Bus, MapPin, Users, Route, Plus, UserPlus, Search, Trash2, Pencil,
  Phone, IdCard, DollarSign, Calendar, Key, CheckCircle, AlertCircle
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import DriverTransportPage from './DriverTransportPage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Student Transport View Component
const StudentTransportView = ({ getAuthHeader, user }) => {
  const [myTransport, setMyTransport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyTransport();
  }, []);

  const fetchMyTransport = async () => {
    try {
      if (user?.role === 'student') {
        // Get student's own transport
        const res = await axios.get(`${API_URL}/api/transport-v2/my-route`, { 
          headers: getAuthHeader() 
        });
        const data = res.data;
        if (data.has_transport && data.allocation) {
          // Extract data from nested structure
          setMyTransport({
            student_name: data.allocation.student_name,
            route_name: data.route?.name || data.allocation.route_name,
            pickup_stop_name: data.allocation.pickup_stop_name,
            pickup_time: data.stops?.[0]?.arrival_time || data.route?.morning_start_time,
            vehicle_number: data.vehicle?.vehicle_number || data.route?.vehicle_number,
            vehicle_type: data.vehicle?.vehicle_type,
            driver_name: data.driver?.name,
            driver_phone: data.driver?.phone,
            monthly_fee: data.allocation.monthly_fee || data.route?.monthly_fee || 0,
            distance_km: data.allocation.distance_km
          });
        } else {
          setMyTransport(null);
        }
      } else if (user?.role === 'parent') {
        // Get children's transport
        const childrenRes = await axios.get(`${API_URL}/api/students/parent/children`, {
          headers: getAuthHeader()
        });
        const children = childrenRes.data || [];
        if (children.length > 0) {
          // Get first child's transport
          const child = children[0];
          const transportRes = await axios.get(`${API_URL}/api/transport-v2/student/${child.id}`, {
            headers: getAuthHeader()
          });
          if (transportRes.data && transportRes.data.route_id) {
            setMyTransport({
              ...transportRes.data,
              student_name: child.name
            });
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch transport data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="My Transport">
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="My Transport">
      <div className="space-y-6">
        {myTransport ? (
          <>
            {/* Transport Details Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bus className="h-5 w-5" />
                  My Transport Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    {myTransport.student_name && (
                      <div>
                        <Label className="text-muted-foreground">Student</Label>
                        <p className="text-lg font-semibold">{myTransport.student_name}</p>
                      </div>
                    )}
                    <div>
                      <Label className="text-muted-foreground">Route</Label>
                      <p className="text-lg font-semibold">{myTransport.route_name || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Pickup Stop</Label>
                      <p className="font-medium">{myTransport.pickup_stop_name || myTransport.stop_name || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Pickup Time</Label>
                      <p className="font-medium">{myTransport.pickup_time || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-muted-foreground">Vehicle</Label>
                      <p className="font-medium">{myTransport.vehicle_number || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Driver</Label>
                      <p className="font-medium">{myTransport.driver_name || 'Not Assigned'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Driver Contact</Label>
                      <p className="font-medium">{myTransport.driver_phone || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Fee Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Transport Fee
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-2xl font-bold">₹{myTransport.monthly_fee || 0}</p>
                    <p className="text-sm text-muted-foreground">Per Month</p>
                  </div>
                  <Badge variant="outline">
                    {myTransport.fee_status || 'Active'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <Bus className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Transport Allocated</h3>
              <p className="text-muted-foreground">
                You do not have any transport allocation yet. 
                Please contact the school office for transport registration.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

const TransportPageV2Admin = () => {
  const { getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState({});
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [stops, setStops] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [students, setStudents] = useState([]);
  const [staffList, setStaffList] = useState([]);  // For Driver/Attendant selection
  
  // Dialogs
  const [vehicleDialog, setVehicleDialog] = useState(false);
  const [driverDialog, setDriverDialog] = useState(false);
  const [routeDialog, setRouteDialog] = useState(false);
  const [stopDialog, setStopDialog] = useState(false);
  const [allocateDialog, setAllocateDialog] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  
  // Edit mode states
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [editingDriver, setEditingDriver] = useState(null);
  const [editingRoute, setEditingRoute] = useState(null);
  const [editingStop, setEditingStop] = useState(null);
  
  // Forms
  const [vehicleForm, setVehicleForm] = useState({
    vehicle_number: '', vehicle_type: 'bus', capacity: 40, make: '', insurance_expiry: ''
  });
  const [driverForm, setDriverForm] = useState({
    staff_id: '', name: '', phone: '', email: '', license_number: '', license_expiry: '', role: 'driver', salary: 0
  });
  const [routeForm, setRouteForm] = useState({
    route_name: '', route_number: '', start_point: '', end_point: '', distance_km: 0,
    estimated_time_minutes: 60, fixed_fee: 0, per_km_fee: 0, vehicle_id: '', driver_id: '', attendant_id: ''
  });
  const [stopForm, setStopForm] = useState({
    route_id: '', stop_name: '', stop_order: 1, arrival_time: '07:00', distance_from_school_km: 0, landmark: '', monthly_fee: ''
  });
  const [allocateForm, setAllocateForm] = useState({
    student_id: '', route_id: '', pickup_stop_id: '', transport_type: 'both'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashRes, vehRes, drvRes, rtRes, stopRes, allocRes, studRes, staffRes] = await Promise.all([
        axios.get(`${API_URL}/api/transport-v2/dashboard`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/transport-v2/vehicles`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/transport-v2/drivers`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/transport-v2/routes`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/transport-v2/stops`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/transport-v2/allocations`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/transport-v2/students-list`, { headers: getAuthHeader() }),  // Use transport-specific students list
        axios.get(`${API_URL}/api/staff`, { headers: getAuthHeader() })
      ]);
      setDashboard(dashRes.data);
      // Handle paginated responses - extract items array
      setVehicles(vehRes.data.items || vehRes.data);
      setDrivers(drvRes.data.items || drvRes.data);
      setRoutes(rtRes.data.items || rtRes.data);
      setStops(stopRes.data.items || stopRes.data);
      setAllocations(allocRes.data.items || allocRes.data);
      const studentData = studRes.data.items || studRes.data;
      setStudents(Array.isArray(studentData) ? studentData : []);
      // Staff for Driver/Attendant selection
      const staffData = staffRes.data?.items || staffRes.data || [];
      setStaffList(Array.isArray(staffData) ? staffData.filter(s => s.status === 'active' || !s.status) : []);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const createVehicle = async () => {
    try {
      await axios.post(`${API_URL}/api/transport-v2/vehicles`, vehicleForm, { headers: getAuthHeader() });
      toast.success('Vehicle added');
      setVehicleDialog(false);
      setVehicleForm({ vehicle_number: '', vehicle_type: 'bus', capacity: 40, make: '', insurance_expiry: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add vehicle');
    }
  };

  // Update Vehicle
  const updateVehicle = async () => {
    try {
      await axios.put(`${API_URL}/api/transport-v2/vehicles/${editingVehicle}`, vehicleForm, { headers: getAuthHeader() });
      toast.success('Vehicle updated');
      closeVehicleDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update vehicle');
    }
  };

  // Delete Vehicle
  const deleteVehicle = async (id, hasStudents) => {
    if (hasStudents) {
      toast.error('Cannot delete vehicle with assigned students. Remove students first.');
      return;
    }
    if (!window.confirm('Delete this vehicle?')) return;
    try {
      await axios.delete(`${API_URL}/api/transport-v2/vehicles/${id}`, { headers: getAuthHeader() });
      toast.success('Vehicle deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete vehicle');
    }
  };

  // Open Edit Vehicle
  const openEditVehicle = (v) => {
    setEditingVehicle(v.id);
    setVehicleForm({
      vehicle_number: v.vehicle_number || '',
      vehicle_type: v.vehicle_type || 'bus',
      capacity: v.capacity || 40,
      make: v.make || '',
      insurance_expiry: v.insurance_expiry || ''
    });
    setVehicleDialog(true);
  };

  // Close Vehicle Dialog
  const closeVehicleDialog = () => {
    setVehicleDialog(false);
    setEditingVehicle(null);
    setVehicleForm({ vehicle_number: '', vehicle_type: 'bus', capacity: 40, make: '', insurance_expiry: '' });
  };

  const createDriver = async () => {
    if (!driverForm.staff_id) {
      toast.error('Please select a staff member');
      return;
    }
    try {
      const response = await axios.post(`${API_URL}/api/transport-v2/drivers`, driverForm, { headers: getAuthHeader() });
      
      // Show login credentials if generated
      if (response.data.login_credentials) {
        const creds = response.data.login_credentials;
        toast.success(
          <div>
            <p className="font-bold">Driver added with login account!</p>
            <p className="text-sm mt-1">Email: {creds.email}</p>
            <p className="text-sm">Password: {creds.password}</p>
            <p className="text-xs text-yellow-600 mt-1">⚠️ Save this password!</p>
          </div>,
          { duration: 15000 }
        );
      } else {
        toast.success(`${driverForm.role === 'driver' ? 'Driver' : 'Attendant'} added`);
      }
      
      setDriverDialog(false);
      setDriverForm({ staff_id: '', name: '', phone: '', email: '', license_number: '', license_expiry: '', role: 'driver', salary: 0 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add');
    }
  };

  // Generate credentials for all drivers without login accounts
  const generateDriverCredentials = async () => {
    if (!window.confirm('This will generate login accounts for ALL drivers without accounts. Continue?')) {
      return;
    }
    try {
      const response = await axios.post(
        `${API_URL}/api/transport-v2/drivers/bulk-generate-credentials`,
        {},
        { headers: getAuthHeader() }
      );
      
      if (response.data.created > 0) {
        // Show credentials in a modal or downloadable format
        const credList = response.data.credentials.map(c => 
          `${c.driver_name}: ${c.login_email} / ${c.password}`
        ).join('\n');
        
        toast.success(
          <div className="max-h-60 overflow-y-auto">
            <p className="font-bold mb-2">Created {response.data.created} login accounts!</p>
            <pre className="text-xs bg-muted p-2 rounded">{credList}</pre>
            <p className="text-xs text-yellow-600 mt-2">⚠️ Copy and save these credentials!</p>
          </div>,
          { duration: 30000 }
        );
        
        // Also log to console for easy copying
        console.log('Driver Credentials:', response.data.credentials);
      } else {
        toast.info(response.data.message);
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate credentials');
    }
  };

  // Reset Driver Password
  const resetDriverPassword = async (driverId, driverName) => {
    if (!window.confirm(`Reset password for ${driverName}?`)) {
      return;
    }
    try {
      const response = await axios.post(
        `${API_URL}/api/transport-v2/drivers/${driverId}/reset-password`,
        {},
        { headers: getAuthHeader() }
      );
      
      const creds = response.data.login_credentials;
      toast.success(
        <div>
          <p className="font-bold mb-2">Password Reset for {driverName}</p>
          <p className="text-sm">Email: {creds.email}</p>
          <p className="text-sm font-mono bg-muted px-2 py-1 rounded mt-1">Password: {creds.password}</p>
          <p className="text-xs text-yellow-600 mt-2">⚠️ Save this password!</p>
        </div>,
        { duration: 20000 }
      );
      
      console.log('Reset Password:', creds);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  // Update Driver
  const updateDriver = async () => {
    try {
      await axios.put(`${API_URL}/api/transport-v2/drivers/${editingDriver}`, driverForm, { headers: getAuthHeader() });
      toast.success('Staff updated');
      closeDriverDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  // Delete Driver
  const deleteDriver = async (id, assignedToRoute) => {
    if (assignedToRoute) {
      toast.error('Cannot delete staff assigned to a route. Unassign first.');
      return;
    }
    if (!window.confirm('Delete this staff member?')) return;
    try {
      await axios.delete(`${API_URL}/api/transport-v2/drivers/${id}`, { headers: getAuthHeader() });
      toast.success('Staff deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  // Open Edit Driver
  const openEditDriver = (d) => {
    setEditingDriver(d.id);
    setDriverForm({
      staff_id: d.staff_id || '',
      name: d.name || '',
      phone: d.phone || '',
      license_number: d.license_number || '',
      license_expiry: d.license_expiry || '',
      role: d.role || 'driver',
      salary: d.salary || 0
    });
    setDriverDialog(true);
  };

  // Close Driver Dialog
  const closeDriverDialog = () => {
    setDriverDialog(false);
    setEditingDriver(null);
    setDriverForm({ staff_id: '', name: '', phone: '', license_number: '', license_expiry: '', role: 'driver', salary: 0 });
  };

  const createRoute = async () => {
    try {
      await axios.post(`${API_URL}/api/transport-v2/routes`, routeForm, { headers: getAuthHeader() });
      toast.success('Route created');
      setRouteDialog(false);
      setRouteForm({ route_name: '', route_number: '', start_point: '', end_point: '', distance_km: 0,
        estimated_time_minutes: 60, fixed_fee: 0, per_km_fee: 0, vehicle_id: '', driver_id: '', attendant_id: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create route');
    }
  };

  // Update Route
  const updateRoute = async () => {
    try {
      await axios.put(`${API_URL}/api/transport-v2/routes/${editingRoute}`, routeForm, { headers: getAuthHeader() });
      toast.success('Route updated');
      closeRouteDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update route');
    }
  };

  // Delete Route
  const deleteRoute = async (id, studentCount) => {
    if (studentCount > 0) {
      toast.error('Cannot delete route with assigned students. Remove all students first.');
      return;
    }
    if (!window.confirm('Delete this route? All stops will also be deleted.')) return;
    try {
      await axios.delete(`${API_URL}/api/transport-v2/routes/${id}`, { headers: getAuthHeader() });
      toast.success('Route deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete route');
    }
  };

  // Open Edit Route
  const openEditRoute = (r) => {
    setEditingRoute(r.id);
    setRouteForm({
      route_name: r.route_name || '',
      route_number: r.route_number || '',
      start_point: r.start_point || '',
      end_point: r.end_point || '',
      distance_km: r.distance_km || 0,
      estimated_time_minutes: r.estimated_time_minutes || 60,
      fixed_fee: r.fixed_fee || 0,
      per_km_fee: r.per_km_fee || 0,
      vehicle_id: r.vehicle_id || '',
      driver_id: r.driver_id || '',
      attendant_id: r.attendant_id || ''
    });
    setRouteDialog(true);
  };

  // Close Route Dialog
  const closeRouteDialog = () => {
    setRouteDialog(false);
    setEditingRoute(null);
    setRouteForm({ route_name: '', route_number: '', start_point: '', end_point: '', distance_km: 0,
      estimated_time_minutes: 60, fixed_fee: 0, per_km_fee: 0, vehicle_id: '', driver_id: '', attendant_id: '' });
  };

  const createStop = async () => {
    try {
      await axios.post(`${API_URL}/api/transport-v2/stops`, stopForm, { headers: getAuthHeader() });
      toast.success('Stop added');
      setStopDialog(false);
      setStopForm({ route_id: '', stop_name: '', stop_order: 1, arrival_time: '07:00', distance_from_school_km: 0, landmark: '', monthly_fee: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add stop');
    }
  };

  // Update Stop
  const updateStop = async () => {
    try {
      await axios.put(`${API_URL}/api/transport-v2/stops/${editingStop}`, stopForm, { headers: getAuthHeader() });
      toast.success('Stop updated');
      closeStopDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update stop');
    }
  };

  // Delete Stop
  const deleteStop = async (id, studentCount) => {
    if (studentCount > 0) {
      toast.error('Cannot delete stop with assigned students. Remove all students first.');
      return;
    }
    if (!window.confirm('Delete this stop?')) return;
    try {
      await axios.delete(`${API_URL}/api/transport-v2/stops/${id}`, { headers: getAuthHeader() });
      toast.success('Stop deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete stop');
    }
  };

  // Open Edit Stop
  const openEditStop = (s) => {
    setEditingStop(s.id);
    setStopForm({
      route_id: s.route_id || '',
      stop_name: s.stop_name || '',
      stop_order: s.stop_order || 1,
      arrival_time: s.arrival_time || '07:00',
      distance_from_school_km: s.distance_from_school_km || 0,
      landmark: s.landmark || '',
      monthly_fee: s.monthly_fee || ''
    });
    setStopDialog(true);
  };

  // Close Stop Dialog
  const closeStopDialog = () => {
    setStopDialog(false);
    setEditingStop(null);
    setSelectedRoute(null);
    setStopForm({ route_id: '', stop_name: '', stop_order: 1, arrival_time: '07:00', distance_from_school_km: 0, landmark: '', monthly_fee: '' });
  };

  const allocateStudent = async () => {
    try {
      await axios.post(`${API_URL}/api/transport-v2/allocate`, allocateForm, { headers: getAuthHeader() });
      toast.success('Student allocated to transport');
      setAllocateDialog(false);
      setAllocateForm({ student_id: '', route_id: '', pickup_stop_id: '', transport_type: 'both' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to allocate');
    }
  };

  const removeAllocation = async (id) => {
    if (!window.confirm('Remove student from transport?')) return;
    try {
      const res = await axios.delete(`${API_URL}/api/transport-v2/allocations/${id}`, { headers: getAuthHeader() });
      if (res.data?.status === 'blocked') {
        const pendingAmt = res.data.pending_amount || 0;
        const pendingCount = res.data.pending_count || 0;
        const forceRemove = window.confirm(
          `Pending Transport Fees: ${pendingCount} (₹${pendingAmt.toLocaleString()})\n\nForce remove will DELETE all pending fees.\n\nConfirm delete?`
        );
        if (forceRemove) {
          const reason = window.prompt('Reason (e.g., Wrong entry, Duplicate):');
          if (!reason || !reason.trim()) {
            toast.error('Reason is required');
            return;
          }
          await axios.delete(
            `${API_URL}/api/transport-v2/allocations/${id}?force_remove=true&write_off_reason=${encodeURIComponent(reason.trim())}`,
            { headers: getAuthHeader() }
          );
          toast.success('Student removed & pending fees deleted');
          fetchData();
        }
        return;
      }
      toast.success('Removed from transport');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove');
    }
  };

  const stats = [
    { title: 'Vehicles', value: dashboard.total_vehicles || 0, icon: Bus, color: 'bg-blue-500' },
    { title: 'Routes', value: dashboard.total_routes || 0, icon: Route, color: 'bg-purple-500' },
    { title: 'Drivers', value: dashboard.total_drivers || 0, icon: Users, color: 'bg-green-500' },
    { title: 'Attendants', value: dashboard.total_attendants || 0, icon: Users, color: 'bg-amber-500' },
    { title: 'Students', value: dashboard.total_students || 0, icon: UserPlus, color: 'bg-pink-500' },
    { title: 'Stops', value: dashboard.total_stops || 0, icon: MapPin, color: 'bg-indigo-500' },
  ];

  return (
    <DashboardLayout title="Transport Management">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-6 w-full max-w-3xl">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="vehicles">Vehicles</TabsTrigger>
          <TabsTrigger value="staff">Staff</TabsTrigger>
          <TabsTrigger value="routes">Routes</TabsTrigger>
          <TabsTrigger value="stops">Stops</TabsTrigger>
          <TabsTrigger value="students">Students</TabsTrigger>
        </TabsList>

        {/* Dashboard */}
        <TabsContent value="dashboard" className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {stats.map((stat, i) => {
              const Icon = stat.icon;
              return (
                <motion.div key={stat.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-xs font-medium text-muted-foreground">{stat.title}</CardTitle>
                      <div className={`${stat.color} p-2 rounded-lg`}><Icon className="h-3.5 w-3.5 text-white" /></div>
                    </CardHeader>
                    <CardContent><div className="text-2xl font-bold">{stat.value}</div></CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Fee Collection (This Month)</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between"><span className="text-muted-foreground">Total:</span><span className="font-bold">₹{dashboard.fee_summary?.total || 0}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Collected:</span><span className="font-bold text-green-600">₹{dashboard.fee_summary?.collected || 0}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Pending:</span><span className="font-bold text-red-600">₹{dashboard.fee_summary?.pending || 0}</span></div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader><CardTitle className="text-base">Quick Actions</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <Button className="w-full justify-start" variant="outline" onClick={() => setVehicleDialog(true)}><Bus className="h-4 w-4 mr-2" />Add Vehicle</Button>
                <Button className="w-full justify-start" variant="outline" onClick={() => setDriverDialog(true)}><Users className="h-4 w-4 mr-2" />Add Driver/Attendant</Button>
                <Button className="w-full justify-start" variant="outline" onClick={() => setRouteDialog(true)}><Route className="h-4 w-4 mr-2" />Create Route</Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Vehicles */}
        <TabsContent value="vehicles" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Vehicles ({vehicles.length})</h3>
            <Button onClick={() => setVehicleDialog(true)} data-testid="add-vehicle-btn"><Plus className="h-4 w-4 mr-2" />Add Vehicle</Button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {vehicles.map(v => (
              <Card key={v.id} data-testid={`vehicle-${v.vehicle_number}`}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div><CardTitle className="text-base">{v.vehicle_number}</CardTitle><CardDescription className="capitalize">{v.vehicle_type}</CardDescription></div>
                    <Badge variant={v.status === 'active' ? 'default' : 'secondary'}>{v.status}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Capacity:</span><span>{v.capacity} seats</span></div>
                  {v.driver_name && <div className="flex justify-between"><span className="text-muted-foreground">Driver:</span><span>{v.driver_name}</span></div>}
                  {v.make && <div className="flex justify-between"><span className="text-muted-foreground">Make:</span><span>{v.make}</span></div>}
                  {v.insurance_expiry && <div className="flex justify-between"><span className="text-muted-foreground">Insurance:</span><span>{v.insurance_expiry}</span></div>}
                  <div className="flex gap-2 pt-2 border-t">
                    <Button size="sm" variant="outline" onClick={() => openEditVehicle(v)}><Pencil className="h-3 w-3 mr-1" />Edit</Button>
                    <Button size="sm" variant="destructive" onClick={() => deleteVehicle(v.id, v.student_count > 0)} disabled={v.student_count > 0}>
                      <Trash2 className="h-3 w-3 mr-1" />Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Staff (Drivers/Attendants) */}
        <TabsContent value="staff" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Drivers & Attendants ({drivers.length})</h3>
            <div className="flex gap-2">
              <Button variant="outline" onClick={generateDriverCredentials} data-testid="generate-credentials-btn">
                <Key className="h-4 w-4 mr-2" />Generate All Logins
              </Button>
              <Button onClick={() => setDriverDialog(true)} data-testid="add-driver-btn"><Plus className="h-4 w-4 mr-2" />Add Staff</Button>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {drivers.map(d => (
              <Card key={d.id}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div><CardTitle className="text-base">{d.name}</CardTitle><CardDescription className="capitalize">{d.role}</CardDescription></div>
                    <Badge variant={d.role === 'driver' ? 'default' : 'secondary'}>{d.role}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex items-center gap-2"><Phone className="h-3 w-3" />{d.phone}</div>
                  {d.email && <div className="flex items-center gap-2 text-green-600"><CheckCircle className="h-3 w-3" />Login: {d.email}</div>}
                  {!d.email && !d.user_id && <div className="flex items-center gap-2 text-amber-600"><AlertCircle className="h-3 w-3" />No login account</div>}
                  {d.role === 'driver' && d.license_number && (
                    <div className="flex items-center gap-2"><IdCard className="h-3 w-3" />{d.license_number}</div>
                  )}
                  {d.license_expiry && <div className="flex items-center gap-2"><Calendar className="h-3 w-3" />Expires: {d.license_expiry}</div>}
                  {d.assigned_vehicle && <div className="flex items-center gap-2"><Bus className="h-3 w-3" />{d.assigned_vehicle}</div>}
                  <div className="flex gap-2 pt-2 border-t flex-wrap">
                    {(d.email || d.user_id) && (
                      <Button size="sm" variant="outline" onClick={() => resetDriverPassword(d.id, d.name)}>
                        <Key className="h-3 w-3 mr-1" />Reset Password
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => openEditDriver(d)}><Pencil className="h-3 w-3 mr-1" />Edit</Button>
                    <Button size="sm" variant="destructive" onClick={() => deleteDriver(d.id, d.assigned_route)} disabled={d.assigned_route}>
                      <Trash2 className="h-3 w-3 mr-1" />Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Routes */}
        <TabsContent value="routes" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Routes ({routes.length})</h3>
            <Button onClick={() => setRouteDialog(true)} data-testid="add-route-btn"><Plus className="h-4 w-4 mr-2" />Create Route</Button>
          </div>
          <div className="space-y-4">
            {routes.map(r => (
              <Card key={r.id} data-testid={`route-${r.route_number}`}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-base">{r.route_name} ({r.route_number})</CardTitle>
                      <CardDescription>{r.start_point} → {r.end_point}</CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Badge variant="outline">{r.stop_count || 0} stops</Badge>
                      <Badge>{r.student_count || 0} students</Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div><span className="text-muted-foreground">Distance:</span> {r.distance_km} km</div>
                    <div><span className="text-muted-foreground">Time:</span> {r.estimated_time_minutes} min</div>
                    <div><span className="text-muted-foreground">Fixed Fee:</span> ₹{r.fixed_fee}</div>
                    <div><span className="text-muted-foreground">Per km:</span> ₹{r.per_km_fee}</div>
                  </div>
                  {(r.vehicle_number || r.driver_name) && (
                    <div className="flex gap-4 text-sm mt-2">
                      {r.vehicle_number && <span><Bus className="h-3 w-3 inline mr-1" />{r.vehicle_number}</span>}
                      {r.driver_name && <span><Users className="h-3 w-3 inline mr-1" />{r.driver_name}</span>}
                    </div>
                  )}
                  <div className="flex gap-2 pt-2 border-t">
                    <Button size="sm" variant="outline" onClick={() => { setSelectedRoute(r.id); setStopForm({...stopForm, route_id: r.id}); setStopDialog(true); }}>
                      <Plus className="h-3 w-3 mr-1" />Add Stop
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openEditRoute(r)}><Pencil className="h-3 w-3 mr-1" />Edit</Button>
                    <Button size="sm" variant="destructive" onClick={() => deleteRoute(r.id, r.student_count)} disabled={r.student_count > 0}>
                      <Trash2 className="h-3 w-3 mr-1" />Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Stops */}
        <TabsContent value="stops" className="space-y-4">
          <h3 className="text-lg font-semibold">All Stops ({stops.length})</h3>
          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="p-3 text-left text-sm font-medium">#</th>
                    <th className="p-3 text-left text-sm font-medium">Stop Name</th>
                    <th className="p-3 text-left text-sm font-medium">Route</th>
                    <th className="p-3 text-left text-sm font-medium">Distance</th>
                    <th className="p-3 text-left text-sm font-medium">Fee</th>
                    <th className="p-3 text-left text-sm font-medium">Time</th>
                    <th className="p-3 text-left text-sm font-medium">Students</th>
                    <th className="p-3 text-left text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {stops.map(s => (
                    <tr key={s.id} className="border-t">
                      <td className="p-3">{s.stop_order}</td>
                      <td className="p-3 font-medium">{s.stop_name}</td>
                      <td className="p-3">{s.route_name}</td>
                      <td className="p-3">{s.distance_from_school_km} km</td>
                      <td className="p-3">{s.monthly_fee ? `₹${s.monthly_fee}` : '-'}</td>
                      <td className="p-3">{s.arrival_time}</td>
                      <td className="p-3">{s.student_count || 0}</td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openEditStop(s)}><Pencil className="h-3 w-3" /></Button>
                          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteStop(s.id, s.student_count)} disabled={s.student_count > 0}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Students */}
        <TabsContent value="students" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Transport Students ({allocations.length})</h3>
            <Button onClick={() => setAllocateDialog(true)} data-testid="allocate-student-btn"><UserPlus className="h-4 w-4 mr-2" />Allocate Student</Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="p-3 text-left text-sm font-medium">Student</th>
                    <th className="p-3 text-left text-sm font-medium">Class</th>
                    <th className="p-3 text-left text-sm font-medium">Route</th>
                    <th className="p-3 text-left text-sm font-medium">Stop</th>
                    <th className="p-3 text-left text-sm font-medium">Type</th>
                    <th className="p-3 text-left text-sm font-medium">Fee</th>
                    <th className="p-3 text-left text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allocations.map(a => (
                    <tr key={a.id} className="border-t">
                      <td className="p-3 font-medium">{a.student_name}</td>
                      <td className="p-3">{a.class_name}</td>
                      <td className="p-3">{a.route_name}</td>
                      <td className="p-3">{a.pickup_stop_name}</td>
                      <td className="p-3 capitalize">{a.transport_type}</td>
                      <td className="p-3">₹{a.monthly_fee}</td>
                      <td className="p-3">
                        <Button size="sm" variant="destructive" onClick={() => removeAllocation(a.id)}><Trash2 className="h-4 w-4" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Vehicle Dialog */}
      <Dialog open={vehicleDialog} onOpenChange={closeVehicleDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingVehicle ? 'Edit Vehicle' : 'Add Vehicle'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Vehicle Number *</Label><Input value={vehicleForm.vehicle_number} onChange={e => setVehicleForm({...vehicleForm, vehicle_number: e.target.value})} placeholder="WB-XX-XXXX" /></div>
            <div><Label>Type</Label>
              <Select value={vehicleForm.vehicle_type} onValueChange={v => setVehicleForm({...vehicleForm, vehicle_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bus">Bus</SelectItem>
                  <SelectItem value="van">Van</SelectItem>
                  <SelectItem value="auto">Auto</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Capacity</Label><Input type="number" value={vehicleForm.capacity} onChange={e => setVehicleForm({...vehicleForm, capacity: parseInt(e.target.value)})} /></div>
            <div><Label>Make/Brand</Label><Input value={vehicleForm.make} onChange={e => setVehicleForm({...vehicleForm, make: e.target.value})} placeholder="e.g., Tata" /></div>
            <div><Label>Insurance Expiry</Label><Input type="date" value={vehicleForm.insurance_expiry} onChange={e => setVehicleForm({...vehicleForm, insurance_expiry: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeVehicleDialog}>Cancel</Button>
            <Button onClick={editingVehicle ? updateVehicle : createVehicle}>{editingVehicle ? 'Update' : 'Add'} Vehicle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Driver/Attendant Dialog */}
      <Dialog open={driverDialog} onOpenChange={closeDriverDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingDriver ? 'Edit' : 'Add'} Driver/Attendant</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Role</Label>
              <Select value={driverForm.role} onValueChange={v => setDriverForm({...driverForm, role: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="driver">Driver</SelectItem>
                  <SelectItem value="attendant">Attendant</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Select Staff *</Label>
              <Select 
                value={driverForm.staff_id} 
                onValueChange={v => {
                  const selectedStaff = staffList.find(s => s.id === v);
                  setDriverForm({
                    ...driverForm, 
                    staff_id: v, 
                    name: selectedStaff?.name || '',
                    phone: selectedStaff?.phone || selectedStaff?.mobile || ''
                  });
                }}
              >
                <SelectTrigger><SelectValue placeholder="Select Staff" /></SelectTrigger>
                <SelectContent>
                  {staffList.map(staff => (
                    <SelectItem key={staff.id} value={staff.id}>
                      {staff.name} {staff.designation ? `(${staff.designation})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {staffList.length === 0 && (
                <p className="text-xs text-muted-foreground mt-1">No staff found. Add staff first.</p>
              )}
            </div>
            {driverForm.staff_id && (
              <div className="grid grid-cols-2 gap-4 p-3 bg-muted/50 rounded-lg">
                <div>
                  <Label className="text-xs text-muted-foreground">Name</Label>
                  <p className="font-medium">{driverForm.name}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Phone</Label>
                  <p className="font-medium">{driverForm.phone || 'N/A'}</p>
                </div>
              </div>
            )}
            {/* Email for Login Account */}
            <div>
              <Label>Email (for App Login)</Label>
              <Input 
                type="email" 
                value={driverForm.email} 
                onChange={e => setDriverForm({...driverForm, email: e.target.value})} 
                placeholder="driver@example.com"
              />
              <p className="text-xs text-muted-foreground mt-1">Required for Driver App login. Leave empty if not needed.</p>
            </div>
            {driverForm.role === 'driver' && (
              <>
                <div><Label>License Number *</Label><Input value={driverForm.license_number} onChange={e => setDriverForm({...driverForm, license_number: e.target.value})} placeholder="e.g., DL-1234567890" /></div>
                <div><Label>License Expiry *</Label><Input type="date" value={driverForm.license_expiry} onChange={e => setDriverForm({...driverForm, license_expiry: e.target.value})} /></div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDriverDialog}>Cancel</Button>
            <Button onClick={editingDriver ? updateDriver : createDriver}>{editingDriver ? 'Update' : 'Add'} {driverForm.role === 'driver' ? 'Driver' : 'Attendant'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Route Dialog */}
      <Dialog open={routeDialog} onOpenChange={closeRouteDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingRoute ? 'Edit Route' : 'Create Route'}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Route Name *</Label><Input value={routeForm.route_name} onChange={e => setRouteForm({...routeForm, route_name: e.target.value})} placeholder="e.g., North Route" /></div>
              <div><Label>Route Number *</Label><Input value={routeForm.route_number} onChange={e => setRouteForm({...routeForm, route_number: e.target.value})} placeholder="e.g., R1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Start Point</Label><Input value={routeForm.start_point} onChange={e => setRouteForm({...routeForm, start_point: e.target.value})} /></div>
              <div><Label>End Point</Label><Input value={routeForm.end_point} onChange={e => setRouteForm({...routeForm, end_point: e.target.value})} placeholder="School" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Total Distance (km)</Label><Input type="number" value={routeForm.distance_km} onChange={e => setRouteForm({...routeForm, distance_km: parseFloat(e.target.value)})} /></div>
              <div><Label>Est. Time (min)</Label><Input type="number" value={routeForm.estimated_time_minutes} onChange={e => setRouteForm({...routeForm, estimated_time_minutes: parseInt(e.target.value)})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Fixed Fee (₹/month)</Label><Input type="number" value={routeForm.fixed_fee} onChange={e => setRouteForm({...routeForm, fixed_fee: parseFloat(e.target.value)})} /></div>
              <div><Label>Per km Fee (₹)</Label><Input type="number" value={routeForm.per_km_fee} onChange={e => setRouteForm({...routeForm, per_km_fee: parseFloat(e.target.value)})} /></div>
            </div>
            <div><Label>Assign Vehicle</Label>
              <Select value={routeForm.vehicle_id} onValueChange={v => setRouteForm({...routeForm, vehicle_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select vehicle" /></SelectTrigger>
                <SelectContent>
                  {vehicles.filter(v => v.status === 'active').map(v => <SelectItem key={v.id} value={v.id}>{v.vehicle_number} ({v.capacity} seats)</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Assign Driver</Label>
              <Select value={routeForm.driver_id} onValueChange={v => setRouteForm({...routeForm, driver_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select driver" /></SelectTrigger>
                <SelectContent>
                  {drivers.filter(d => d.role === 'driver').map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Assign Attendant</Label>
              <Select value={routeForm.attendant_id} onValueChange={v => setRouteForm({...routeForm, attendant_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select attendant" /></SelectTrigger>
                <SelectContent>
                  {drivers.filter(d => d.role === 'attendant').map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeRouteDialog}>Cancel</Button>
            <Button onClick={editingRoute ? updateRoute : createRoute}>{editingRoute ? 'Update' : 'Create'} Route</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Stop Dialog */}
      <Dialog open={stopDialog} onOpenChange={closeStopDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingStop ? 'Edit Stop' : 'Add Stop to Route'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Route</Label>
              <Select value={stopForm.route_id} onValueChange={v => setStopForm({...stopForm, route_id: v})} disabled={!!editingStop}>
                <SelectTrigger><SelectValue placeholder="Select route" /></SelectTrigger>
                <SelectContent>
                  {routes.map(r => <SelectItem key={r.id} value={r.id}>{r.route_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Stop Name *</Label><Input value={stopForm.stop_name} onChange={e => setStopForm({...stopForm, stop_name: e.target.value})} placeholder="e.g., Main Market" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Order</Label><Input type="number" value={stopForm.stop_order} onChange={e => setStopForm({...stopForm, stop_order: parseInt(e.target.value)})} min={1} /></div>
              <div><Label>Arrival Time</Label><Input type="time" value={stopForm.arrival_time} onChange={e => setStopForm({...stopForm, arrival_time: e.target.value})} /></div>
            </div>
            <div><Label>Distance from School (km)</Label><Input type="number" step="0.1" value={stopForm.distance_from_school_km} onChange={e => setStopForm({...stopForm, distance_from_school_km: parseFloat(e.target.value)})} /></div>
            <div><Label>Monthly Fee (₹)</Label><Input type="number" value={stopForm.monthly_fee} onChange={e => setStopForm({...stopForm, monthly_fee: e.target.value ? parseFloat(e.target.value) : ''})} placeholder="Stop-wise fee (optional)" /></div>
            <div><Label>Landmark</Label><Input value={stopForm.landmark} onChange={e => setStopForm({...stopForm, landmark: e.target.value})} placeholder="Near post office" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeStopDialog}>Cancel</Button>
            <Button onClick={editingStop ? updateStop : createStop}>{editingStop ? 'Update' : 'Add'} Stop</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Allocate Student Dialog */}
      <Dialog open={allocateDialog} onOpenChange={setAllocateDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Allocate Student to Transport</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Student</Label>
              <Select value={allocateForm.student_id} onValueChange={v => setAllocateForm({...allocateForm, student_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select student" /></SelectTrigger>
                <SelectContent>
                  {students.filter(s => !s.transport_route).map(s => <SelectItem key={s.id} value={s.id}>{s.name} ({s.student_id})</SelectItem>)}
                  {students.filter(s => !s.transport_route).length === 0 && (
                    <div className="p-2 text-sm text-muted-foreground">No unallocated students</div>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Route</Label>
              <Select value={allocateForm.route_id} onValueChange={v => setAllocateForm({...allocateForm, route_id: v, pickup_stop_id: ''})}>
                <SelectTrigger><SelectValue placeholder="Select route" /></SelectTrigger>
                <SelectContent>
                  {routes.map(r => <SelectItem key={r.id} value={r.id}>{r.route_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Pickup Stop</Label>
              <Select value={allocateForm.pickup_stop_id} onValueChange={v => setAllocateForm({...allocateForm, pickup_stop_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select stop" /></SelectTrigger>
                <SelectContent>
                  {stops.filter(s => s.route_id === allocateForm.route_id).map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.stop_name} ({s.distance_from_school_km} km) {s.monthly_fee ? `- ₹${s.monthly_fee}/month` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Transport Type</Label>
              <Select value={allocateForm.transport_type} onValueChange={v => setAllocateForm({...allocateForm, transport_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">Both (Pickup & Drop)</SelectItem>
                  <SelectItem value="pickup">Pickup Only</SelectItem>
                  <SelectItem value="drop">Drop Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={allocateStudent}>Allocate</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

// Main Component with Role-based Routing
const TransportPageWrapper = () => {
  const { getAuthHeader, user, userPermissions } = useAuth();
  
  // Student/Parent gets simplified view
  if (['student', 'parent'].includes(user?.role)) {
    return <StudentTransportView getAuthHeader={getAuthHeader} user={user} />;
  }
  
  // Check if user is a Driver (staff with only transport access, not full admin)
  // Driver role = staff with preset_driver role or only transport module access
  const isDriver = user?.role === 'staff' && 
    (userPermissions?.role_name === 'Driver' || 
     (userPermissions?.modules?.includes('transport') && 
      !userPermissions?.has_full_access && 
      userPermissions?.modules?.length <= 2));
  
  if (isDriver) {
    return <DriverTransportPage />;
  }
  
  // Admin/Transport Manager gets full view
  return <TransportPageV2Admin />;
};

export default TransportPageWrapper;
